import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  CloseIcon,
  TableComponent,
  DisplayModal,
  DisplayArrayModal
} from 'components';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button
} from '@material-ui/core';
import { eventHistoryTableTitles } from 'constants.js';
import { actions } from 'store/eventReports';
import { actions as surveyActions } from 'store/surveys';
import {
  updateTotalCount,
  updateRowsPerPage
} from 'store/helpers/tableAndToolbar/actions';
import { ccToPcConverter, getFilteredList } from '../../../helpers/commonUtils';
import { getEnvVar } from 'helpers';
import LicenseCheck from 'LicenseCheck';
import MessageDetails from 'views/Reports/MessageDetails/MessageDetails';

const { getPaginatedEventHistory, getEventLog } = actions;
const {
  updateEventIdFilter,
  updateSurveyHashFilter,
  updateSurveyResultsDetails,
  getPaginatedSurveys
} = surveyActions;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '0px'
  },
  labelDiv: {
    display: 'flex',
    margin: '8px 8px'
  },
  innerKeyDiv: {
    width: '400px',
    fontWeight: 500
  },
  innerValueDiv: {
    width: '400px'
  }
}));

const EventDetails = () => {
  const licenses = LicenseCheck();
  const { hasFeatureSurvey } = licenses;
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const eventDetail = useSelector((store) => store.eventReports.current);
  const eventHistory = useSelector((store) => store.eventReports.eventHistory);
  const isLoading = useSelector((store) => store.eventReports.gettingPaginated);
  const totalCount = useSelector((store) => store.tableAndToolbar.totalCount);
  const currentEventId = useSelector((store) => store.survey.eventId);
  const surveyHashFilter = useSelector(
    (store) => store.survey.surveyHashFilter
  );
  const surveyResultDetails = useSelector(
    (store) => store.survey.surveyResultDetails
  );
  const isGettingSurveyResultsLoading = useSelector(
    (store) => store.survey.gettingSurveyResults
  );
  const gettingEventLog = useSelector(
    (store) => store.eventReports.gettingEventLog
  );
  const eventLog = useSelector((store) => store.eventReports.eventLog);
  const eventLogError = useSelector(
    (store) => store.eventReports.eventLogError
  );

  const [currentEventIdentifierDetail, setCurrentEventIdentifierDetail] =
    useState({});
  const [currentEventDetail, setCurrentEventDetail] = useState({});
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isEventLogModalOpen, setIsEventLogModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [currentResults, setCurrentResults] = useState({});
  const [messageId, setMessageId] = useState('');
  const [emptyResultsMessage, setEmptyResultsMessage] = React.useState({
    show: false,
    msg: 'No survey linked to this campaign.'
  });
  const [emptyEventLogMessage, setEmptyEventLogMessage] = React.useState({
    show: false,
    msg: 'No event log available for this event.'
  });
  const keysToFilter =
    (getEnvVar('ADM_EVENT_DETAIL_FIELDS') || '').split(',') || [];
  const eventLogDisplayLimit = getEnvVar('ADM_EVENT_LOG_DISPLAY_LIMIT') || 10;
  const eventLogInfoMessage = `Event logs for the past ${eventLogDisplayLimit} events are displayed.  Contact Agilysys to modify the limit.`;

  const navBack = () => {
    history.push('/events');
  };

  useEffect(() => {
    if (!Object.keys(eventDetail).length > 0) {
      history.push('/events');
    }
    dispatch(updateRowsPerPage(10));
    dispatch(updateTotalCount(eventDetail?.eventHistory?.length));
    setCurrentEventDetail(getFilteredList(eventDetail, keysToFilter));
    setCurrentEventIdentifierDetail(eventDetail?.eventIdentifierValues);
    dispatch(
      getPaginatedEventHistory({
        force: true,
        eventHistory: eventDetail?.eventHistory
      })
    );
  }, []);

  useEffect(() => {
    if (currentEventId) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        };
      });
    }
  }, [currentEventId]);

  useEffect(() => {
    if (surveyHashFilter.length === 0) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: true
        };
      });
      updateSurveyResultsDetails({});
    } else {
      dispatch(getPaginatedSurveys({ force: true, forEvents: true }));
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        };
      });
    }
  }, [surveyHashFilter]);

  useEffect(() => {
    if (Object.keys(surveyResultDetails).length === 0) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: true
        };
      });
      setCurrentResults({});
    } else {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        };
      });
      setCurrentResults(surveyResultDetails);
    }
  }, [surveyResultDetails]);

  useEffect(() => {
    if (eventLogError?.message && eventLog.length === 0) {
      setEmptyEventLogMessage({
        show: true,
        msg: eventLogError.message
      });
    }
  }, [eventLogError, eventLog]);

  const onClickLink = (tableTitle, row) => {
    switch (tableTitle.value) {
      case 'campaignUuid':
        return window.open(`campaigns/edit/${row[tableTitle.value]}`, '_blank');
      case 'communicationTemplateUuid':
        return window.open(
          `templates/details/${row[tableTitle.value]}`,
          '_blank'
        );
      case 'messageId':
        return window.open(
          `messages/details/${row[tableTitle.value]}`,
          '_blank'
        );
    }
  };

  const getActionButtons = (row) => [
    ...(hasFeatureSurvey()
      ? [
          {
            name: 'View Survey Results',
            action: ['ACTION', () => onClickViewSurveyResults(row)],
            isDisabled: row.campaignUuid ? false : true
          }
        ]
      : []),
    {
      name: row.messageId
        ? row.isExpired
          ? 'Message Archived'
          : 'View Message'
        : 'View Message',
      action: ['ACTION', () => onClickViewMessage(row)],
      isDisabled: row.messageId ? (row.isExpired ? true : false) : true,
      tooltipTitle: row.messageId
        ? row.isExpired
          ? 'Message deleted'
          : 'View message content'
        : 'Message is not available'
    }
  ];

  const onClickViewSurveyResults = (row) => {
    setIsResultsModalOpen(true);
    dispatch(updateSurveyHashFilter([]));
    dispatch(updateSurveyResultsDetails({}));
    dispatch(
      updateEventIdFilter({
        eventId: eventDetail.eventId,
        campaignUuid: row.campaignUuid
      })
    );
  };

  const onclickViewEventLog = (eventDetail) => {
    let eventId = eventDetail?.eventId;
    let partialCollectionName = eventDetail?.partialCollectionName;
    setIsEventLogModalOpen(true);
    if (eventId) {
      setEmptyEventLogMessage({
        show: false,
        msg: ''
      });
      dispatch(
        getEventLog({
          eventId: eventId,
          partialCollectionName: partialCollectionName
        })
      );
    } else {
      setEmptyEventLogMessage({
        show: true,
        msg: 'Event ID is not available for this event.'
      });
    }
  };

  const onClickViewMessage = (row) => {
    setMessageId(row.messageId);
    setIsMessageModalOpen(true);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <CloseIcon className={classes.closeIcon} onClickClose={navBack} />
      </div>
      {currentEventDetail && Object.keys(currentEventDetail)?.length > 0 && (
        <div className={classes.content}>
          <Card>
            <CardHeader
              title="Event Details"
              titleTypographyProps={{ variant: 'h3' }}
            />
            <Divider />
            <CardContent>
              {Object.keys(currentEventDetail).map((prop) => {
                return (
                  <div className={classes.labelDiv}>
                    <div className={classes.innerKeyDiv}>
                      {ccToPcConverter(prop)}
                    </div>
                    {currentEventDetail[prop] &&
                    typeof currentEventDetail[prop] === 'object' &&
                    currentEventDetail[prop]?.length > 0 ? (
                      currentEventDetail[prop].map((item, index) => (
                        <div key={index} className={classes.innerValueDiv}>
                          {item}
                        </div>
                      ))
                    ) : (
                      <div className={classes.innerValueDiv}>
                        {currentEventDetail[prop] || '-'}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}
      {currentEventIdentifierDetail &&
        Object.keys(currentEventIdentifierDetail)?.length > 0 && (
          <div className={classes.content}>
            <Card>
              <CardHeader
                title="Event Identifier Details"
                titleTypographyProps={{ variant: 'h3' }}
              />
              <Divider />
              <CardContent>
                {Object.keys(currentEventIdentifierDetail)?.map((prop) => {
                  return (
                    <div className={classes.labelDiv}>
                      <div className={classes.innerKeyDiv}>
                        {ccToPcConverter(prop)}
                      </div>
                      {currentEventIdentifierDetail[prop] &&
                      typeof currentEventIdentifierDetail[prop] === 'object' &&
                      currentEventIdentifierDetail[prop]?.length > 0 ? (
                        currentEventIdentifierDetail[prop].map(
                          (item, index) => (
                            <div key={index} className={classes.innerValueDiv}>
                              {item}
                            </div>
                          )
                        )
                      ) : (
                        <div className={classes.innerValueDiv}>
                          {currentEventIdentifierDetail[prop] || '-'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}
      {totalCount && totalCount > 0 && (
        <div className={classes.content}>
          <Card>
            <CardHeader
              title="Event History Details"
              titleTypographyProps={{ variant: 'h3' }}
              action={
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    onclickViewEventLog(eventDetail);
                  }}
                >
                  View Event Logs
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <TableComponent
                onClickLink={onClickLink}
                isLoading={isLoading}
                tableTitles={eventHistoryTableTitles}
                rowsCount={totalCount}
                actionButtonOptions={(row) => getActionButtons(row)}
                filteredRows={eventHistory}
                action={getPaginatedEventHistory({
                  force: true,
                  eventHistory: eventDetail?.eventHistory
                })}
              />
            </CardContent>
          </Card>
        </div>
      )}
      <DisplayModal
        batch={currentResults}
        title={'Linked Surveys'}
        isOpen={isResultsModalOpen}
        setIsOpen={setIsResultsModalOpen}
        emptyMessage={emptyResultsMessage}
        isConversionRequired={true}
        size={'xs'}
        isTable={true}
        isLoading={isGettingSurveyResultsLoading}
      />
      <DisplayArrayModal
        list={eventLog}
        title={'Event Log'}
        isOpen={isEventLogModalOpen}
        setIsOpen={setIsEventLogModalOpen}
        emptyMessage={emptyEventLogMessage}
        isConversionRequired={true}
        size={'lg'}
        isLoading={gettingEventLog}
        infoMessage={eventLogInfoMessage}
      />
      <DisplayModal
        batch={{}}
        title={'View Message Details'}
        isOpen={isMessageModalOpen}
        setIsOpen={setIsMessageModalOpen}
        isConversionRequired={true}
        minWidth="700px"
        minHeight="600px"
        showOpenTabIcon={true}
        onOpenTabClick={() =>
          window.open(`messages/details/${messageId}`, '_blank')
        }
        customComponent={
          <MessageDetails isModal={true} messageId={messageId} />
        }
      />
    </div>
  );
};

export default EventDetails;
